/**
 * Presentation payload → the injected Studio stylesheet. Rule order encodes
 * Word's cascade so plain CSS specificity resolves it:
 *
 *   docDefaults  →  .studio-doc (rPr) / .studio-doc block selectors (pPr)
 *   default style →  same block selectors, later in the sheet
 *   named paragraph styles → [data-style-id="X"]        (higher specificity)
 *   character styles       → [data-r-style="X"]         (later: rStyle > pStyle)
 *   table styles           → table[data-tstyle="X"] regions, region order =
 *                            wholeTable < row bands < column bands < first/
 *                            last column < first/last row < corners, as Word
 *                            applies them (the TableRegions plugin assigns
 *                            the ts-* classes per cell, tableRegions.js)
 *
 * Direct formatting (verbatim pPr/rPr fragments → inline styles) beats all
 * of it, exactly like Word.
 */

import { fontFamilyCss } from './fontStack.js'
import { fontMetrics } from './lineMetrics.js'
import { fillTone } from './ooxmlCss.js'
import { borderPropCss, paragraphPropsToCss, runPropsToCss } from './propsCss.js'

const BLOCKS = '.studio-doc p, .studio-doc h1, .studio-doc h2, .studio-doc h3, .studio-doc h4, .studio-doc h5, .studio-doc h6'

function rule(selector, css) {
  return css ? `${selector} { ${css} }\n` : ''
}

/**
 * Where a table-style region's own edges are, per edge: [the cells on the
 * region's outline, the cells inside it] as a selector suffix (each one
 * class-level, so every region rule has the same specificity and the rule
 * order decides, as in Word). A row region (header, total, row band) is
 * outlined left and right by the row's first and last cell, a column region
 * top and bottom by the table's first and last row, a corner is one cell.
 * Measured in Word.app 16.112: Light List's band1Horz draws its left and
 * right only at the ends of the row; between the cells the table's insideV
 * shows (the malls' "Võrdlustehingute valik" tables).
 */
const ROW_EDGES = {
  top: [':not(.ts-in-top)', '.ts-in-top'],
  bottom: [':not(.ts-in-bottom)', '.ts-in-bottom'],
  left: ['.tb-first-col', ':not(.tb-first-col)'],
  right: ['.tb-last-col', ':not(.tb-last-col)'],
}
const COLUMN_EDGES = {
  top: ['.tb-first-row', ':not(.tb-first-row)'],
  bottom: ['.tb-last-row', ':not(.tb-last-row)'],
  left: [':not(.ts-in-left)', '.ts-in-left'],
  right: [':not(.ts-in-right)', '.ts-in-right'],
}
const TABLE_EDGES = {
  top: ['.tb-first-row', ':not(.tb-first-row)'],
  bottom: ['.tb-last-row', ':not(.tb-last-row)'],
  left: ['.tb-first-col', ':not(.tb-first-col)'],
  right: ['.tb-last-col', ':not(.tb-last-col)'],
}
// A corner cell is always on that table edge, which keeps its rules as specific as the others.
const cornerEdges = (tableEdge) => ({ top: [tableEdge, null], bottom: [tableEdge, null], left: [tableEdge, null], right: [tableEdge, null] })
const REGION_EDGES = {
  'ts-band1-h': ROW_EDGES,
  'ts-band2-h': ROW_EDGES,
  'ts-first-row': ROW_EDGES,
  'ts-last-row': ROW_EDGES,
  'ts-band1-v': COLUMN_EDGES,
  'ts-band2-v': COLUMN_EDGES,
  'ts-first-col': COLUMN_EDGES,
  'ts-last-col': COLUMN_EDGES,
  'ts-nw': cornerEdges('.tb-first-row'),
  'ts-ne': cornerEdges('.tb-first-row'),
  'ts-sw': cornerEdges('.tb-last-row'),
  'ts-se': cornerEdges('.tb-last-row'),
}
const INSIDE_EDGE = { top: 'insideH', bottom: 'insideH', left: 'insideV', right: 'insideV' }

/** One table-style region → rules under the given cell selector. */
function tableRegionCss(styleId, regionClass, region) {
  if (!region) return ''
  // th = a cell of a repeated header row (tableHeader): styled like td.
  const cellSelector = regionClass
    ? `.studio-doc table[data-tstyle="${styleId}"] :is(td, th).${regionClass}`
    : `.studio-doc table[data-tstyle="${styleId}"] :is(td, th)`
  let out = ''

  const cellCss = []
  const tc = region.tcPr
  if (tc) {
    if (/^#[0-9A-Fa-f]{6}$/.test(tc.shd?.fill ?? '')) cellCss.push(`background-color: ${tc.shd.fill}`)
    const vAlign = { top: 'top', center: 'middle', bottom: 'bottom' }[tc.vAlign]
    if (vAlign) cellCss.push(`vertical-align: ${vAlign}`)
  }
  out += rule(cellSelector, cellCss.join('; '))
  // The region's cell borders beat the table's own tblBorders in Word, all
  // nil or all single alike (measured in Word.app 16.112), except on an edge
  // the cell's own tcBorders names (data-tc-edges, renderCell): there the
  // cell decides, and a `none sz=0` leaves the table's border to draw. The
  // malls' styled tables name every edge that way. A region's top / left /
  // bottom / right are its outline; between its cells it draws its
  // insideH / insideV, or nothing of its own (REGION_EDGES).
  const shape = regionClass ? REGION_EDGES[regionClass] : TABLE_EDGES
  for (const edge of ['top', 'left', 'bottom', 'right']) {
    const [outline, inside] = shape[edge]
    const own = `:not([data-tc-edges~="${edge}"])`
    const border = borderPropCss(tc?.tcBorders?.[edge])
    if (border) out += rule(`${cellSelector}${outline}${own}`, `border-${edge}: ${border}`)
    const insideBorder = inside ? borderPropCss(tc?.tcBorders?.[INSIDE_EDGE[edge]]) : null
    if (insideBorder) out += rule(`${cellSelector}${inside}${own}`, `border-${edge}: ${insideBorder}`)
  }
  // Word's automatic text colour on the region's fill: white on a dark one,
  // back to the inherited colour on a light one (a later region's fill wins,
  // as the fill itself does). In a cascade layer, so any colour the styles
  // or the runs name (unlayered) still wins, and a cell's own fill (the
  // data-fill-tone rules, studio.css's later layer 'direct') beats it.
  const tone = fillTone(tc?.shd?.fill)
  if (tone) {
    const css = tone === 'dark' ? '--studio-auto-color: #fff; color: #fff' : '--studio-auto-color: #000; color: inherit'
    out += `@layer studio-auto-color.region { ${cellSelector} { ${css} } }\n`
  }

  // Region run/paragraph formatting applies to the cell's content.
  out += rule(`${cellSelector}`, runPropsToCss(region.rPr))
  out += rule(`${cellSelector} p`, paragraphPropsToCss(region.pPr))

  return out
}

/** Whole-table region extras that live on the table box, not the cells. */
function tableBoxCss(styleId, region, tableSelector = `.studio-doc table[data-tstyle="${styleId}"]`) {
  if (!region) return ''
  const css = []
  const borders = region.tblBorders
  if (borders) {
    const names = { top: '--sb-top', right: '--sb-right', bottom: '--sb-bottom', left: '--sb-left', insideH: '--sb-insideH', insideV: '--sb-insideV' }
    for (const [edge, prop] of Object.entries(names)) {
      const border = borderPropCss(borders[edge])
      if (border && border !== 'none') css.push(`${prop}: ${border}`)
    }
  }
  const mar = region.tblCellMarTw
  if (mar) {
    for (const edge of ['top', 'left', 'bottom', 'right']) {
      if (mar[edge] !== undefined) css.push(`--sb-pad-${edge}: ${Number(mar[edge]) / 20}pt`)
    }
  }
  return rule(tableSelector, css.join('; '))
}

/** Conditional region → the ts-* class TableRegions assigns, in Word's order of application. */
const REGION_CLASSES = [
  ['band1Horz', 'ts-band1-h'],
  ['band2Horz', 'ts-band2-h'],
  ['band1Vert', 'ts-band1-v'],
  ['band2Vert', 'ts-band2-v'],
  ['firstCol', 'ts-first-col'],
  ['lastCol', 'ts-last-col'],
  ['firstRow', 'ts-first-row'],
  ['lastRow', 'ts-last-row'],
  ['nwCell', 'ts-nw'],
  ['neCell', 'ts-ne'],
  ['swCell', 'ts-sw'],
  ['seCell', 'ts-se'],
]

/** Full presentation payload → stylesheet text (empty string when absent). */
export function buildPresentationCss(presentation) {
  if (!presentation) return ''
  let out = ''

  const docDefaults = presentation.docDefaults
  if (docDefaults) {
    // Where neither the defaults nor a style say otherwise, Word draws 10 pt
    // text and no paragraph spacing — not studio.css's base (11 pt, 6 pt
    // after), which stands in only when there is no payload at all.
    const spacing = docDefaults.pPr?.spacing ?? {}
    const wordFallbackRun = docDefaults.rPr?.szPt > 0 ? '' : 'font-size: 10pt'
    const wordFallbackBlock = [spacing.beforeTw === undefined ? 'margin-top: 0' : '', spacing.afterTw === undefined ? 'margin-bottom: 0' : '']
    out += rule('.studio-doc', [wordFallbackRun, runPropsToCss(docDefaults.rPr)].filter(Boolean).join('; '))
    out += rule(BLOCKS, [...wordFallbackBlock, paragraphPropsToCss(docDefaults.pPr)].filter(Boolean).join('; '))
    // Word's own table defaults: no space after the table, Normal Table's
    // cell margins (none above and below, 108 twips at the sides).
    out += rule('.studio-doc table.studio-table', 'margin-bottom: 0; --sb-pad-top: 0pt; --sb-pad-right: 5.4pt; --sb-pad-bottom: 0pt; --sb-pad-left: 5.4pt')
  }

  out += rule('.studio-doc', themeFontsCss(presentation.theme?.fonts))

  // The body section's left page margin (the last section is the body's):
  // a page-anchored floating table counts its offset from the page edge
  // (ooxmlCss floatingTableCss).
  const marginLeftTw = Number(presentation.sections?.at(-1)?.marginLeftTw)
  if (Number.isFinite(marginLeftTw) && marginLeftTw >= 0) out += rule('.studio-doc', `--studio-margin-left: ${marginLeftTw / 20}pt`)

  const styles = presentation.styles ?? {}

  // A table without w:tblStyle takes the default table style, as in Word.
  const defaultTable = Object.values(styles.table ?? {}).find((style) => style.isDefault)
  if (defaultTable) out += tableBoxCss(null, defaultTable.wholeTable, '.studio-doc table.studio-table:not([data-tstyle])')

  const paragraphStyles = styles.paragraph ?? {}
  for (const style of Object.values(paragraphStyles)) {
    if (style.isDefault) {
      out += rule(BLOCKS, [paragraphPropsToCss(style.pPr), runPropsToCss(style.rPr)].filter(Boolean).join('; '))
    }
  }
  for (const [styleId, style] of Object.entries(paragraphStyles)) {
    const css = [paragraphPropsToCss(style.pPr), runPropsToCss(style.rPr)].filter(Boolean).join('; ')
    out += rule(`.studio-doc [data-style-id="${styleId}"]`, css)
  }

  out += contextualSpacingCss(paragraphStyles)

  for (const [styleId, style] of Object.entries(styles.character ?? {})) {
    out += rule(`.studio-doc [data-r-style="${styleId}"]`, runPropsToCss(style.rPr))
  }

  for (const [styleId, style] of Object.entries(styles.table ?? {})) {
    out += tableBoxCss(styleId, style.wholeTable)
    out += tableRegionCss(styleId, null, style.wholeTable)
    for (const [regionKey, regionClass] of REGION_CLASSES) {
      out += tableRegionCss(styleId, regionClass, style.conditional?.[regionKey])
    }
  }

  out += numberingMarkerCss(presentation.numbering)

  return out
}

const PARAGRAPHS = ':is(p, h1, h2, h3, h4, h5, h6)'

/**
 * w:contextualSpacing: between two paragraphs of the same style, a paragraph
 * that has it (from its style or its own pPr, data-contextual-spacing) loses
 * its space on that side — measured in Word.app: each paragraph's own setting
 * decides only its own side. A paragraph without a pStyle is in the default
 * style. Word then lets the other side's space stand as the part that exceeds
 * the dropped one; the canvas keeps it whole (rare: one style, mixed settings).
 */
function contextualSpacingCss(paragraphStyles) {
  let out = ''
  for (const [styleId, style] of Object.entries(paragraphStyles)) {
    const same = style.isDefault ? `:is([data-style-id="${styleId}"], :not([data-style-id]))` : `[data-style-id="${styleId}"]`
    const flagged = style.pPr?.contextualSpacing ? `${same}:not([data-contextual-spacing="0"])` : `${same}[data-contextual-spacing="1"]`
    out += rule(`.studio-doc ${PARAGRAPHS}${same} + ${PARAGRAPHS}${flagged}`, 'margin-top: 0 !important')
    out += rule(`.studio-doc ${PARAGRAPHS}${flagged}:has(+ ${PARAGRAPHS}${same})`, 'margin-bottom: 0 !important')
  }
  return out
}

/**
 * The theme's major and minor typefaces, for runs that name only a theme slot
 * (lineMetrics.js themeFontCss): the font stack and Word's line metrics.
 */
function themeFontsCss(fonts) {
  const css = []
  for (const slot of ['major', 'minor']) {
    const name = fonts?.[slot]?.latin
    const metrics = fontMetrics(name)
    if (!metrics) continue
    css.push(
      `--studio-font-${slot}: ${fontFamilyCss(name)}`,
      `--studio-${slot}-asc: ${metrics.ascent}`,
      `--studio-${slot}-desc: ${metrics.descent}`,
      `--studio-${slot}-gap: ${metrics.lineGap}`,
    )
  }
  return css.join('; ')
}

/**
 * Per-(numId, ilvl) marker formatting → ::before rules matching the classes
 * the numbering plugin assigns. Bullet levels drop the font (Symbol/Wingdings
 * glyphs are mapped to Unicode client-side; the symbol font would garble
 * them), keeping size/weight/colour.
 */
function numberingMarkerCss(numbering) {
  if (!numbering?.nums) return ''
  let out = ''

  for (const [numId, num] of Object.entries(numbering.nums)) {
    const abstract = numbering.abstract?.[String(num.abstractNumId)]
    if (!abstract) continue
    const levels = { ...abstract.levels }
    for (const [ilvl, override] of Object.entries(num.overrides ?? {})) {
      if (override?.lvl) levels[ilvl] = override.lvl
    }

    for (const [ilvl, level] of Object.entries(levels)) {
      const rPr = level.numFmt === 'bullet' ? { ...level.rPr, fonts: undefined } : level.rPr
      out += rule(`.studio-doc .studio-num-${numId}-${ilvl}::before`, runPropsToCss(rPr))
    }
  }

  return out
}
